import React, { useEffect, useReducer, useState, useContext } from "react";

import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  Tooltip,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import GuideOpenButton from "../../components/GuideOpenButton";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { DeleteOutline, Edit, Add, Business } from "@material-ui/icons";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  useViewMode,
  ViewModeToggle,
  RecordsCardsGrid,
  RecordCard,
  RecordCardMeta,
} from "../../components/ViewModeToggle";

const useStyles = makeStyles((theme) => ({
  // ✅ CORREGIDO: MainPaper dinámico según tema
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderRadius: '12px',
    boxShadow: theme.mode === 'dark' ? '0 4px 20px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
  },
  
  // ✅ CORREGIDO: TableHeader dinámico según tema
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    "& .MuiTableCell-head": {
      color: "white",
      fontWeight: "bold",
      fontSize: "0.875rem",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
  },
  
  tableHeaderCell: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "bold",
    fontSize: "0.875rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "none",
    padding: "16px 8px",
  },
  
  // ✅ CORREGIDO: TableRow dinámico según tema
  tableRow: {
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.action.hover,
    },
    "&:nth-of-type(even)": {
      backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.action.selected,
    },
    "& td": {
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${theme.mode === 'dark' ? '#4B5563' : 'rgba(224, 224, 224, 0.5)'}`,
    },
  },
  
  /** Centrado horizontal del contenido en celdas (Color, mensaje, etc.) */
  cellCenterWrap: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },

  // ✅ NUEVO: Estilos para colores rectangulares
  colorIndicator: {
    width: "80px",
    height: "24px",
    borderRadius: "4px",
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "500",
    fontSize: "0.75rem",
    color: "white",
    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
    flexShrink: 0,
  },
  
  // ✅ NUEVO: Estilos para botones de acción
  actionButton: {
    minWidth: "36px",
    height: "36px",
    borderRadius: "8px",
    margin: "0 4px",
    transition: "all 0.2s ease",
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: theme.shadows[4],
    },
  },
  
  editButton: {
    backgroundColor: "#1976d2",
    color: "white",
    "&:hover": {
      backgroundColor: "#1565c0",
    },
  },
  
  deleteButton: {
    backgroundColor: "#d32f2f",
    color: "white",
    "&:hover": {
      backgroundColor: "#c62828",
    },
  },
  
  // ✅ NUEVO: Estilos para el botón principal
  addButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "600",
    padding: "12px 24px",
    borderRadius: "8px",
    textTransform: "none",
    fontSize: "0.875rem",
    boxShadow: theme.shadows[2],
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: theme.shadows[4],
      transform: "translateY(-1px)",
    },
  },
  
  // ✅ CORREGIDO: InfoChip dinámico según tema
  infoChip: {
    backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.grey[100],
    color: theme.palette.text.primary,
    fontWeight: "500",
    fontSize: "0.75rem",
  },
  
  // ✅ NUEVO: Estilos para mensajes de bienvenida
  greetingText: {
    maxWidth: "200px",
    width: "100%",
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    textAlign: "center",
    margin: "0 auto",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      // ✅ CORRECCIÓN (30/12/2025): Actualizar todos los campos del queue, incluyendo orderQueue y greetingMessage
      const updatedState = [...state];
      const oldQueue = updatedState[queueIndex];
      updatedState[queueIndex] = {
        ...oldQueue,
        ...queue, // Actualizar con todos los campos nuevos
        // ✅ Asegurar que orderQueue y greetingMessage se actualicen correctamente
        orderQueue: queue.orderQueue !== undefined && queue.orderQueue !== null ? queue.orderQueue : (oldQueue.orderQueue !== undefined ? oldQueue.orderQueue : null),
        greetingMessage: queue.greetingMessage !== undefined ? queue.greetingMessage : oldQueue.greetingMessage,
        // ✅ CORRECCIÓN: Manejar flowBuilderId explícitamente (incluyendo null para eliminar)
        // Si queue.flowBuilderId está definido (incluso si es null), usarlo. Si no, mantener el valor anterior
        flowBuilderId: queue.hasOwnProperty('flowBuilderId') ? queue.flowBuilderId : (oldQueue.flowBuilderId !== undefined ? oldQueue.flowBuilderId : null),
        // ✅ CORRECCIÓN: También actualizar integrationId y promptId de la misma manera
        integrationId: queue.hasOwnProperty('integrationId') ? queue.integrationId : (oldQueue.integrationId !== undefined ? oldQueue.integrationId : null),
        promptId: queue.hasOwnProperty('promptId') ? queue.promptId : (oldQueue.promptId !== undefined ? oldQueue.promptId : null)
      };
      return updatedState;
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);

  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [viewMode, setViewMode] = useViewMode("queues");

  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);
  const companyId = user?.companyId;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!socketManager || !companyId) {
      return;
    }

    const socket = socketManager.getSocket(companyId);

    if (!socket) {
      return;
    }

    const handleQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    };

    const eventName = `company-${companyId}-queue`;
    socket.on(eventName, handleQueueEvent);

    return () => {
      if (socket && typeof socket.off === 'function') {
        socket.off(eventName, handleQueueEvent);
      }
    };
  }, [socketManager, companyId, dispatch]);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("queues.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setSelectedQueue(null);
  };

  // ✅ FUNCIÓN PARA DETERMINAR TIPO DE AUTOMATIZACIÓN
  const getAutomationType = (queue) => {
    const hasPrompt = queue.promptId !== null && queue.promptId !== undefined;
    const hasIntegration = queue.integrationId !== null && queue.integrationId !== undefined;
    const hasChatbot = queue.options && Array.isArray(queue.options) && queue.options.length > 0;
    const hasFlowBuilder = queue.flowBuilderId !== null && queue.flowBuilderId !== undefined;

    // ✅ Prioridad: FlowBuilder > Prompt > Integración > Chatbot
    if (hasFlowBuilder) {
      return { type: i18n.t("queues.automationLabels.flowbuilder"), color: "#9C27B0" };
    } else if (hasPrompt) {
      return { type: i18n.t("queues.automationLabels.prompt"), color: "#1976d2" };
    } else if (hasIntegration) {
      return { type: i18n.t("queues.automationLabels.integration"), color: "#7b1fa2" };
    } else if (hasChatbot) {
      return { type: i18n.t("queues.automationLabels.chatbot"), color: "#388e3c" };
    } else {
      return { type: i18n.t("queues.automationLabels.none"), color: "#757575" };
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${
            selectedQueue.name
          }?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
      />
      <MainHeader>
        <Title>
          <Box display="flex" alignItems="center">
            <Business style={{ marginRight: 12, color: theme.palette.primary.main }} />
            {i18n.t("queues.title")}
            <span style={{ marginLeft: 8 }}>
              <GuideOpenButton id="queue" title={i18n.t("queues.title")} to="/queues" startId="list" />
            </span>
          </Box>
        </Title>
        <MainHeaderButtonsWrapper>
          <Button
            className={classes.addButton}
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenQueueModal}
          >
            {i18n.t("queues.buttons.add")}
          </Button>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        {viewMode === "cards" ? (
          <RecordsCardsGrid>
            {queues.map((queue) => {
              const automation = getAutomationType(queue);
              return (
                <RecordCard
                  key={`queue-card-${queue.id}`}
                  title={queue.name}
                  actions={
                    <>
                      <IconButton
                        className={`${classes.actionButton} ${classes.editButton}`}
                        size="small"
                        onClick={() => handleEditQueue(queue)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        className={`${classes.actionButton} ${classes.deleteButton}`}
                        size="small"
                        onClick={() => {
                          setSelectedQueue(queue);
                          setConfirmModalOpen(true);
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </>
                  }
                >
                  <RecordCardMeta>ID {queue.id} · {queue.color}</RecordCardMeta>
                  <RecordCardMeta>{automation.type}</RecordCardMeta>
                  <RecordCardMeta>
                    {queue.greetingMessage || i18n.t("queues.placeholders.noGreeting")}
                  </RecordCardMeta>
                </RecordCard>
              );
            })}
          </RecordsCardsGrid>
        ) : (
        <Table size="small">
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("queues.table.id")}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("queues.table.department")}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("queues.table.color")}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("queues.table.automation")}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("queues.table.greeting")}</TableCell>
              <TableCell className={classes.tableHeaderCell} align="center">{i18n.t("queues.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {queues.map((queue) => (
                <TableRow key={queue.id} className={classes.tableRow}>
                  <TableCell align="center">
                    <Chip 
                      label={queue.id}
                      className={classes.infoChip}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" style={{ fontWeight: "500" }}>
                      {queue.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box className={classes.cellCenterWrap}>
                      <div
                        className={classes.colorIndicator}
                        style={{ backgroundColor: queue.color }}
                      >
                        {queue.color}
                      </div>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {(() => {
                      const automation = getAutomationType(queue);
                      return (
                        <Chip
                          label={automation.type}
                          style={{
                            backgroundColor: automation.color,
                            color: "white",
                            fontWeight: "600",
                            fontSize: "0.75rem"
                          }}
                          size="small"
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    <Box className={classes.cellCenterWrap}>
                      <Tooltip title={queue.greetingMessage || i18n.t("queues.placeholders.noGreeting")} arrow>
                        <Typography
                          className={classes.greetingText}
                          noWrap
                          variant="body2"
                          component="div"
                        >
                          {queue.greetingMessage || i18n.t("queues.placeholders.noGreeting")}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={i18n.t("queues.tooltips.editQueue")} arrow>
                      <IconButton
                        className={`${classes.actionButton} ${classes.editButton}`}
                        size="small"
                        onClick={() => handleEditQueue(queue)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("queues.tooltips.deleteQueue")} arrow>
                      <IconButton
                        className={`${classes.actionButton} ${classes.deleteButton}`}
                        size="small"
                        onClick={() => {
                          setSelectedQueue(queue);
                          setConfirmModalOpen(true);
                        }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton columns={6} />}
            </>
          </TableBody>
        </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Queues;
